import React, { useState, useEffect } from "react";

const WORKER_URL = "https://reflex-backend.alfaoir1.workers.dev";

export default function WebsitePricingForms() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`${WORKER_URL}/pricing-forms`);
        const data = await res.json();
        if (!data.success) throw new Error(data.error || "Failed to fetch");
        setSubmissions(data.submissions);
      } catch (err) {
        if (retryCount < 3) {
          setTimeout(() => setRetryCount((c) => c + 1), 2000);
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissions();
  }, [retryCount]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      const res = await fetch(`${WORKER_URL}/pricing-forms/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) setSubmissions((prev) => prev.filter((s) => s._id !== id));
      else alert("Delete failed");
    } catch {
      alert("Delete failed");
    }
  };

  if (loading) return <div style={styles.center}>Loading submissions...</div>;
  if (error) return <div style={styles.center}>Error: {error}</div>;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Website Pricing Form Submissions</h1>
        <span style={styles.count}>{submissions.length} total</span>
      </div>

      {submissions.length === 0 ? (
        <div style={styles.empty}>No submissions yet.</div>
      ) : (
        <div style={styles.grid}>
          {submissions.map((s) => (
            <div key={s._id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div>
                  <div style={styles.name}>{s.name}</div>
                  <div style={styles.package}>{s.package} — {s.packagePrice}</div>
                </div>
                <button style={styles.deleteBtn} onClick={() => handleDelete(s._id)}>Delete</button>
              </div>

              <div style={styles.divider} />

              <div style={styles.row}><span style={styles.label}>Email</span><span style={styles.val}>{s.email}</span></div>
              <div style={styles.row}><span style={styles.label}>Phone</span><span style={styles.val}>{s.phone}</span></div>
              <div style={styles.row}><span style={styles.label}>Company</span><span style={styles.val}>{s.company || "—"}</span></div>
              <div style={styles.row}><span style={styles.label}>Website</span><span style={styles.val}>{s.website || "—"}</span></div>
              {s.note && (
                <div style={styles.noteWrap}>
                  <div style={styles.label}>Note</div>
                  <div style={styles.note}>{s.note}</div>
                </div>
              )}
              <div style={styles.date}>
                {s.submittedAt ? new Date(s.submittedAt).toLocaleString() : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#f5f0ff", padding: "40px 24px", fontFamily: "sans-serif" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", maxWidth: 1100, margin: "0 auto 32px" },
  title: { fontSize: 26, fontWeight: 800, color: "#0a0a0a", margin: 0 },
  count: { background: "#7b19bc", color: "#fff", borderRadius: 20, padding: "4px 14px", fontSize: 13, fontWeight: 700 },
  grid: { maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 20 },
  card: { background: "#fff", borderRadius: 14, padding: "24px 22px", border: "1px solid #ede8f5", boxShadow: "0 2px 12px rgba(123,25,188,0.07)" },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 },
  name: { fontSize: 17, fontWeight: 800, color: "#0a0a0a" },
  package: { fontSize: 12, fontWeight: 600, color: "#7b19bc", marginTop: 3 },
  deleteBtn: { background: "#fff0f0", color: "#cc0000", border: "1px solid #ffcccc", borderRadius: 7, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  divider: { height: 1, background: "#ede8f5", margin: "14px 0" },
  row: { display: "flex", justifyContent: "space-between", marginBottom: 8 },
  label: { fontSize: 11, fontWeight: 700, color: "#b09cc5", textTransform: "uppercase", letterSpacing: "0.08em" },
  val: { fontSize: 13, color: "#333", fontWeight: 500, textAlign: "right", maxWidth: "60%" },
  noteWrap: { marginTop: 10 },
  note: { fontSize: 13, color: "#555", marginTop: 4, lineHeight: 1.6, background: "#faf9ff", borderRadius: 8, padding: "8px 10px" },
  date: { fontSize: 11, color: "#bbb", marginTop: 12, textAlign: "right" },
  center: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", fontSize: 16, color: "#666" },
  empty: { textAlign: "center", color: "#999", fontSize: 16, marginTop: 60 },
};